import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Redirect } from 'react-router-dom';
import { Route, Routes } from 'react-router-dom-v5-compat';

import { AdvancedShippingNotices } from './advanced-shipping-notices/advanced-shipping-notices';
import { ContextWrappedDashboard } from './dashboard';
import { FulfillmentsLeftNav } from './fulfillments-left-nav';
import style from './fulfilments-page.module.less';
import { messages } from './messages';
import { SetupPage } from './overview';
import { ResourcesPage } from './resources';
import FulfillmentsReturnsPage from './returns';
import Page from '../../components/page';
import { Fulfillments } from '../../components/shipping-grid/views/fulfillments';
import { FulfillmentsCancelled } from '../../components/shipping-grid/views/fulfillments-cancelled';
import { FulfillmentsDelivered } from '../../components/shipping-grid/views/fulfillments-delivered';
import { FulfillmentsDeliveryException } from '../../components/shipping-grid/views/fulfillments-delivery-exception';
import { FulfillmentsInTransit } from '../../components/shipping-grid/views/fulfillments-in-transit';
import { FulfillmentsPending } from '../../components/shipping-grid/views/fulfillments-pending';
import { FulfillmentsRecent } from '../../components/shipping-grid/views/fulfillments-recent';
import { FULFILLMENTS_VIEW_ID } from '../../components/shipping-grid/views/view-id-constants';
import { useEnableFulfillmentsReturns } from '../../feature-flags/hooks/use-enable-fulfillments-returns';
import {
  BASE_FULFILLMENTS_PAGE_PATH,
  FULFILLMENTS_ASN_PATH,
  FULFILLMENTS_DASHBOARD_PATH,
  FULFILLMENTS_GRID_CANCELLED_PATH,
  FULFILLMENTS_GRID_DELIVERED_PATH,
  FULFILLMENTS_GRID_DELIVERY_EXCEPTION_PATH,
  FULFILLMENTS_GRID_IN_TRANSIT_PATH,
  FULFILLMENTS_GRID_PATH,
  FULFILLMENTS_GRID_PENDING_PATH,
  FULFILLMENTS_GRID_RECENT_PATH,
  FULFILLMENTS_RETURNS_PATH,
  FULFILLMENT_RESOURCES_PATH,
} from '../../routes/constants';
import {
  useCanAccessFBSSetup,
  useFulfillmentFTUWizardState,
  useIsFulfillmentASNUnlocked,
} from './overview/fulfilled-by-shipstation-ftu-wizard/use-fulfillment-ftu-wizard-state';

const ShipmentsFulfillments = React.lazy(() => import('./shipments-fulfillments').then((m) => ({ default: m.ShipmentsFulfillments })));

/**
 * @returns {React.JSX.Element}
 */
export const FulfillmentsPage = () => {
  const enableFulfillmentsReturns = useEnableFulfillmentsReturns();
  const isFulfillmentASNUnlocked = useIsFulfillmentASNUnlocked();
  const canAccessFBSSetup = useCanAccessFBSSetup();
  const { userProgress } = useFulfillmentFTUWizardState();
  const isUserSetupComplete = userProgress.isComplete || !canAccessFBSSetup;

  const mainRoutePath = useMemo(() => {
    if (isUserSetupComplete) {
      const pathToDashboard = `${BASE_FULFILLMENTS_PAGE_PATH}/${FULFILLMENTS_DASHBOARD_PATH}`;
      return <Redirect to={pathToDashboard} />;
    }
    return <SetupPage />;
  }, [isUserSetupComplete]);

  const routes = useMemo(() => {
    const items = [
      {
        navItem: {
          id: 'setup',
          label: <FormattedMessage {...messages.setupLeftNavLabel} />,
          location: '',
        },
        Element: <SetupPage />,
        isHidden: isUserSetupComplete,
      },
      {
        navItem: {
          id: 'dashboard',
          label: <FormattedMessage {...messages.dashboardLeftNavLabel} />,
          location: FULFILLMENTS_DASHBOARD_PATH,
        },
        Element: <ContextWrappedDashboard />,
      },
      {
        navItem: {
          id: 'asns',
          label: <FormattedMessage {...messages.asnLeftNavLabel} />,
          location: FULFILLMENTS_ASN_PATH,
        },
        Element: <AdvancedShippingNotices />,
        isHidden: !isFulfillmentASNUnlocked,
      },
      {
        navItem: {
          ...Fulfillments,
          location: FULFILLMENTS_GRID_PATH,
          items: [
            { ...FulfillmentsRecent, location: FULFILLMENTS_GRID_RECENT_PATH },
            { ...FulfillmentsPending, location: FULFILLMENTS_GRID_PENDING_PATH },
            { ...FulfillmentsInTransit, location: FULFILLMENTS_GRID_IN_TRANSIT_PATH },
            { ...FulfillmentsDelivered, location: FULFILLMENTS_GRID_DELIVERED_PATH },
            { ...FulfillmentsDeliveryException, location: FULFILLMENTS_GRID_DELIVERY_EXCEPTION_PATH },
            { ...FulfillmentsCancelled, location: FULFILLMENTS_GRID_CANCELLED_PATH },
          ],
        },
        Element: <ShipmentsFulfillments />,
      },
      {
        navItem: {
          id: 'returns',
          label: <FormattedMessage {...messages.returnsLeftNavLabel} />,
          location: FULFILLMENTS_RETURNS_PATH,
        },
        Element: <FulfillmentsReturnsPage />,
        isHidden: !enableFulfillmentsReturns,
      },
      {
        navItem: {
          id: 'resources',
          label: <FormattedMessage {...messages.resourcesLeftNavLabel} />,
          location: FULFILLMENT_RESOURCES_PATH,
        },
        Element: <ResourcesPage />,
        isHidden: !isUserSetupComplete,
      },
    ];

    return items.filter((item) => !item.isHidden);
  }, [enableFulfillmentsReturns, isFulfillmentASNUnlocked, isUserSetupComplete]);

  const navItems = useMemo(() => routes.map(({ navItem }) => navItem), [routes]);

  return (
    <div className={style['fulfillments-page-wrapper']}>
      <FulfillmentsLeftNav navItems={navItems} />
      <Page>
        <Routes>
          <Route element={mainRoutePath} path='/' />
          {routes.map(({ navItem, Element }) => (
            <Route
              key={navItem.id}
              element={Element}
              path={navItem.id === FULFILLMENTS_VIEW_ID ? `${navItem.location}/*` : navItem.location}
            />
          ))}
        </Routes>
      </Page>
    </div>
  );
};
